import {
    Children,
    forwardRef,
    type ButtonHTMLAttributes,
    type MouseEvent,
    type ReactNode,
} from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { pressHandlers } from "./press";
import { Text } from "./Text";
import pressable from "./pressable.module.css";
import styles from "./Button.module.css";

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Button.
 *
 * Variants:
 *  - `primary`   flat accent fill. One per view region.
 *  - `secondary` tonal fill with a hairline border.
 *  - `ghost`     transparent until hover.
 *  - `soft`      accent tint fill with accent text.
 *  - `danger`    flat danger fill.
 *
 * All state styling lives in `Button.module.css` and is driven by the DOM:
 * `:hover`, `:focus-visible`, `:disabled`, `[aria-busy]`, and `[data-pressed]`
 * (written by `pressHandlers`, never by React state). Press is asymmetric:
 * scale 0.97 in 60ms, release over 220ms. `busy` crossfades the label to a
 * spinner in the same grid cell so the width never changes; the button stays
 * in the tab order but ignores clicks.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "soft" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    disabled?: boolean;
    /** Replace the label with a spinner. The button keeps its width and ignores clicks. */
    busy?: boolean;
    /** Square footprint for a single icon child. */
    iconOnly?: boolean;
    /** Stretch to the width of the container. Stacked dialog footers use it. */
    fullWidth?: boolean;
    children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        variant = "primary",
        size = "md",
        disabled = false,
        busy = false,
        iconOnly = false,
        fullWidth = false,
        className,
        children,
        onClick,
        onPointerDown,
        onPointerUp,
        onPointerCancel,
        onPointerLeave,
        onKeyDown,
        onKeyUp,
        onBlur,
        ...rest
    },
    ref,
) {
    const inert = disabled || busy;
    const press = pressHandlers<HTMLButtonElement>(
        { onPointerDown, onPointerUp, onPointerCancel, onPointerLeave, onKeyDown, onKeyUp, onBlur },
        inert,
    );

    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
        // Keyboard activation still reaches a busy button; swallow it here.
        if (busy) {
            event.preventDefault();
            return;
        }
        onClick?.(event);
    };

    return (
        <button
            ref={ref}
            disabled={disabled}
            aria-busy={busy || undefined}
            data-variant={variant}
            data-size={size}
            data-icon-only={iconOnly || undefined}
            data-full-width={fullWidth || undefined}
            data-hit-area={size === "sm" ? "" : undefined}
            className={cn(pressable.pressable, styles.root, className)}
            onClick={handleClick}
            {...press}
            {...rest}
        >
            <span className={styles.label}>{wrapStringInTextRole(children)}</span>
            {busy && (
                <span aria-hidden="true" className={styles.spinnerSlot}>
                    <span data-spinner="" />
                </span>
            )}
        </button>
    );
});

const inheritedLabelStyle = { color: "inherit", fontSize: "inherit" } as const;

function wrapStringInTextRole(children: ReactNode): ReactNode {
    if (typeof children === "string" || typeof children === "number") {
        return (
            <Text role="button" style={inheritedLabelStyle}>
                {children}
            </Text>
        );
    }
    if (Array.isArray(children)) {
        return Children.map(children, (child, i) => {
            if (typeof child === "string" || typeof child === "number") {
                return (
                    <Text key={`btn-text-${String(i)}`} role="button" style={inheritedLabelStyle}>
                        {child}
                    </Text>
                );
            }
            return child;
        });
    }
    return children;
}
